import Microgame from '../engine/Microgame.js';

export default class AnswerGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Réponse !`, en: `Answer!`, es: `¡Responde!` },
            controls: { fr: `Appuie sur le bon bouton`, en: `Tap the correct button`, es: `Toca el botón correcto` },
            objective: { fr: `Trouve la bonne réponse à la question`, en: `Find the correct answer`, es: `Encuentra la respuesta correcta` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3M12 17h.01"/></svg>`,
            actions: [
                { type: 'wait', duration: 380 },
                { type: 'swipe', fromX: 50, fromY: 86, toX: 82, toY: 86, duration: 280 },
                { type: 'wait', duration: 320 },
            ],
            loopDuration: 3000
        };
    }
    constructor(engine) {
        super(engine);
        this.isDragging = false;
        this.startX = 0;
        this.swipedDirection = null;

        // Randomly swap button positions (the trap!)
        this.swapped = this.isPlayback ? false : Math.random() < 0.2;

        // Reduce time by ~100ms across all multipliers
        const speedMult = engine.speedMultiplier || 1.0;
        const adjustedSpeed = 1 + (speedMult - 1) * (3.1 / 3);
        this.duration = 3000 * (speedMult / adjustedSpeed);
    }

    setup() {
        this.engine.ui.instruction.textContent = "ANSWER!";

        // Phone body
        this.phone = document.createElement('div');
        this.phone.style.position = 'absolute';
        this.phone.style.width = '160px';
        this.phone.style.height = '260px';
        this.phone.style.border = `3px solid ${this.secondaryColor}`;
        this.phone.style.borderRadius = '20px';
        this.phone.style.top = '50%';
        this.phone.style.left = '50%';
        this.phone.style.transform = 'translate(-50%, -50%)';
        this.phone.style.boxShadow = `0 0 20px ${this.secondaryColor}40`;
        this.phone.style.animation = 'shake 0.3s infinite';
        this.phone.style.overflow = 'hidden';
        this.container.appendChild(this.phone);

        // Screen
        const screen = document.createElement('div');
        screen.style.position = 'absolute';
        screen.style.top = '30px';
        screen.style.left = '10px';
        screen.style.right = '10px';
        screen.style.bottom = '70px';
        screen.style.background = `${this.secondaryColor}10`;
        screen.style.borderRadius = '5px';
        screen.style.display = 'flex';
        screen.style.flexDirection = 'column';
        screen.style.justifyContent = 'center';
        screen.style.alignItems = 'center';
        this.phone.appendChild(screen);

        // Phone SVG icon
        const phoneSvg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        phoneSvg.setAttribute('viewBox', '0 0 24 24');
        phoneSvg.setAttribute('width', '50');
        phoneSvg.setAttribute('height', '50');
        phoneSvg.style.marginBottom = '8px';
        phoneSvg.style.filter = `drop-shadow(0 0 6px ${this.primaryColor})`;

        const phonePath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        phonePath.setAttribute('d', 'M6.62 10.79a15.05 15.05 0 006.59 6.59l2.2-2.2a1 1 0 011.01-.24 11.36 11.36 0 003.58.57 1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1 11.36 11.36 0 00.57 3.58 1 1 0 01-.25 1.02l-2.2 2.19z');
        phonePath.setAttribute('fill', this.primaryColor);
        phoneSvg.appendChild(phonePath);
        screen.appendChild(phoneSvg);

        const callerName = document.createElement('div');
        callerName.textContent = 'Incoming...';
        callerName.style.color = this.secondaryColor;
        callerName.style.fontSize = '0.9rem';
        callerName.style.fontWeight = 'bold';
        screen.appendChild(callerName);

        // Button area
        const btnArea = document.createElement('div');
        btnArea.style.position = 'absolute';
        btnArea.style.bottom = '12px';
        btnArea.style.left = '10px';
        btnArea.style.right = '10px';
        btnArea.style.height = '50px';
        btnArea.style.display = 'flex';
        btnArea.style.justifyContent = 'space-between';
        btnArea.style.gap = '10px';
        this.phone.appendChild(btnArea);

        // Decide which side is accept / reject
        const acceptSide = this.swapped ? 'left' : 'right';
        const rejectSide = this.swapped ? 'right' : 'left';

        // Accept button (primary = answer)
        this.acceptBtn = this.createButton('→', this.primaryColor, acceptSide === 'left' ? '←' : '→');
        this.rejectBtn = this.createButton('←', this.secondaryColor, rejectSide === 'left' ? '←' : '→');

        if (this.swapped) {
            btnArea.appendChild(this.acceptBtn);
            btnArea.appendChild(this.rejectBtn);
        } else {
            btnArea.appendChild(this.rejectBtn);
            btnArea.appendChild(this.acceptBtn);
        }

        // Swipe track container (full width below phone)
        this.trackArea = document.createElement('div');
        this.trackArea.style.position = 'absolute';
        this.trackArea.style.bottom = '8%';
        this.trackArea.style.left = '50%';
        this.trackArea.style.transform = 'translateX(-50%)';
        this.trackArea.style.width = '250px';
        this.trackArea.style.height = '60px';
        this.trackArea.style.background = `${this.secondaryColor}15`;
        this.trackArea.style.borderRadius = '30px';
        this.trackArea.style.border = `2px solid ${this.secondaryColor}30`;
        this.trackArea.style.display = 'flex';
        this.trackArea.style.alignItems = 'center';
        this.trackArea.style.justifyContent = 'center';
        this.container.appendChild(this.trackArea);

        // Slider thumb
        this.thumb = document.createElement('div');
        this.thumb.style.width = '50px';
        this.thumb.style.height = '50px';
        this.thumb.style.borderRadius = '50%';
        this.thumb.style.background = this.primaryColor;
        this.thumb.style.boxShadow = `0 0 12px ${this.primaryColor}`;
        this.thumb.style.position = 'absolute';
        this.thumb.style.left = '50%';
        this.thumb.style.top = '50%';
        this.thumb.style.transform = 'translate(-50%, -50%)';
        this.thumb.style.cursor = 'grab';
        this.thumb.style.zIndex = '3';
        this.trackArea.appendChild(this.thumb);

        // Left label
        const leftLabel = document.createElement('div');
        leftLabel.style.position = 'absolute';
        leftLabel.style.left = '12px';
        leftLabel.style.fontSize = '1.2rem';
        leftLabel.style.fontWeight = 'bold';
        leftLabel.style.color = this.swapped ? this.primaryColor : this.secondaryColor;
        leftLabel.style.textShadow = `0 0 8px ${this.swapped ? this.primaryColor : this.secondaryColor}`;
        leftLabel.textContent = this.swapped ? '✓' : '✕';
        this.trackArea.appendChild(leftLabel);

        // Right label
        const rightLabel = document.createElement('div');
        rightLabel.style.position = 'absolute';
        rightLabel.style.right = '12px';
        rightLabel.style.fontSize = '1.2rem';
        rightLabel.style.fontWeight = 'bold';
        rightLabel.style.color = this.swapped ? this.secondaryColor : this.primaryColor;
        rightLabel.style.textShadow = `0 0 8px ${this.swapped ? this.secondaryColor : this.primaryColor}`;
        rightLabel.textContent = this.swapped ? '✕' : '✓';
        this.trackArea.appendChild(rightLabel);

        this.thumbOffset = 0;
        this.trackWidth = 250;

        this.onDown = this.onDown.bind(this);
        this.onMove = this.onMove.bind(this);
        this.onUp = this.onUp.bind(this);

        this.engine.input.element.addEventListener('pointerdown', this.onDown);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
        this.engine.input.element.addEventListener('pointerup', this.onUp);
    }

    createButton(arrow, color, dir) {
        const btn = document.createElement('div');
        btn.style.flex = '1';
        btn.style.height = '100%';
        btn.style.borderRadius = '25px';
        btn.style.border = `2px solid ${color}`;
        btn.style.display = 'flex';
        btn.style.justifyContent = 'center';
        btn.style.alignItems = 'center';
        btn.style.fontSize = '1.2rem';
        btn.style.color = color;
        btn.style.fontWeight = 'bold';
        btn.style.textShadow = `0 0 6px ${color}`;
        btn.textContent = dir;
        return btn;
    }

    onDown(e) {
        if (!this.active) return;
        const rect = this.thumb.getBoundingClientRect();
        const margin = 20;
        if (
            e.clientX >= rect.left - margin &&
            e.clientX <= rect.right + margin &&
            e.clientY >= rect.top - margin &&
            e.clientY <= rect.bottom + margin
        ) {
            this.isDragging = true;
            this.startX = e.clientX;
            this.thumb.style.cursor = 'grabbing';
        }
    }

    onMove(e) {
        if (!this.active || !this.isDragging) return;

        const deltaX = e.clientX - this.startX;
        const maxSlide = (this.trackWidth - 50) / 2;
        const clampedX = Math.max(-maxSlide, Math.min(maxSlide, deltaX));

        this.thumbOffset = clampedX;
        this.thumb.style.left = `calc(50% + ${clampedX}px)`;

        // Change color based on direction
        if (clampedX > 30) {
            this.thumb.style.background = this.swapped ? this.secondaryColor : this.primaryColor;
            this.thumb.style.boxShadow = `0 0 15px ${this.swapped ? this.secondaryColor : this.primaryColor}`;
        } else if (clampedX < -30) {
            this.thumb.style.background = this.swapped ? this.primaryColor : this.secondaryColor;
            this.thumb.style.boxShadow = `0 0 15px ${this.swapped ? this.primaryColor : this.secondaryColor}`;
        } else {
            this.thumb.style.background = this.primaryColor;
            this.thumb.style.boxShadow = `0 0 12px ${this.primaryColor}`;
        }

        // Trigger at threshold
        if (clampedX >= maxSlide * 0.85) {
            this.isDragging = false;
            if (this.swapped) {
                // Right = reject when swapped
                this.lose();
                this.phone.style.animation = 'none';
                this.phone.style.opacity = '0.4';
            } else {
                // Right = accept normally
                this.win();
                this.phone.style.animation = 'none';
                this.phone.style.boxShadow = `0 0 30px ${this.primaryColor}`;
                this.phone.style.borderColor = this.primaryColor;
            }
        } else if (clampedX <= -maxSlide * 0.85) {
            this.isDragging = false;
            if (this.swapped) {
                // Left = accept when swapped
                this.win();
                this.phone.style.animation = 'none';
                this.phone.style.boxShadow = `0 0 30px ${this.primaryColor}`;
                this.phone.style.borderColor = this.primaryColor;
            } else {
                // Left = reject normally
                this.lose();
                this.phone.style.animation = 'none';
                this.phone.style.opacity = '0.4';
            }
        }
    }

    onUp() {
        if (!this.active) return;
        this.isDragging = false;
        if (!this.won) {
            this.thumb.style.transition = 'left 0.2s, background 0.2s';
            this.thumb.style.left = '50%';
            this.thumb.style.background = this.primaryColor;
            this.thumb.style.boxShadow = `0 0 12px ${this.primaryColor}`;
            this.thumb.style.cursor = 'grab';
            this.thumbOffset = 0;
            setTimeout(() => {
                if (this.thumb) this.thumb.style.transition = 'none';
            }, 200);
        }
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onDown);
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
        this.engine.input.element.removeEventListener('pointerup', this.onUp);
    }
}
